import styled from 'styled-components';
import { tokens } from '../../../public/tokens.js';

const { colors, typography, globals, effects } = tokens;

// sharing all button variants in one place
const buttonVariants = {
    primary: {
        background: tokens.colors.theme['button-color'],
        backgroundHover: tokens.colors.theme['button-hover-color'],
        backgroundActive: tokens.colors.theme['button-active-color'],
        color: colors.neutral.white,
    },
    theme: {
        background: tokens.colors.theme['topbar-color'],
        backgroundHover: tokens.colors.theme['button-hover-color'],
        backgroundActive: tokens.colors.theme['button-active-color'],
        color: colors.neutral.white,
    },
    danger: {
        background: colors.states.alert.default,
        backgroundHover: colors.brand.red['600'],
        backgroundActive: colors.brand.red['700'],
        color: colors.neutral.white,
    },
    secondary: {
        background: colors.neutral.grey['200'],
        backgroundHover: colors.neutral.grey['300'],
        backgroundActive: colors.neutral.grey['400'],
        color: colors.neutral.white,
    },
    dark: {
        background: colors.neutral.grey['900'],
        backgroundHover: colors.neutral.grey['700'],
        backgroundActive: colors.neutral.grey['500'],
        color: colors.neutral.white,
    },
    transparent: {
        background: 'transparent',
        backgroundHover: colors.neutral.grey['200'],
        backgroundActive: colors.neutral.grey['300'],
        color: colors.text.primary,
    },
    white: {
        background: colors.neutral.white,
        backgroundHover: colors.neutral.grey['200'],
        backgroundActive: colors.neutral.grey['300'],
        color: colors.text.primary,
    },
    disabled: {
        background: colors.neutral.grey['500'],
        backgroundHover: colors.neutral.grey['600'],
        backgroundActive: colors.neutral.grey['700'],
        color: colors.neutral.white,
    },
    warning: {
        background: colors.states.warning.default,
        backgroundHover: tokens.colors.theme['button-hover-color'],
        backgroundActive: tokens.colors.theme['button-active-color'],
        color: colors.neutral.white,
    },
    success: {
        background: colors.states.success.default,
        backgroundHover: tokens.colors.theme['button-hover-color'],
        backgroundActive: tokens.colors.theme['button-active-color'],
        color: colors.neutral.white,
    },
};

/**
 * StandardButton - Uses JavaScript token properties
 * @param {'primary'|'danger'|'secondary'|'transparent'|'white'} [variant='primary'] - Button variant
 * @param {boolean} [disabled=false] - Disabled state
 * @param {boolean} [border=false] - Show border
 * @param {boolean} [textOnly=false] - Text only mode (no box, just colored text)
 */
export const StandardButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    flex-wrap: nowrap;
    font-family: ${globals['font-family']};
    font-weight: ${({ fontWeight }) => fontWeight ?? typography.fontWeights.semibold};
    font-size: ${({ fontSize }) => fontSize ?? typography.fontSize['lg-14px']};
    cursor: ${(props) => (props.disabled ? 'not-allowed' : props.noAction ? 'default' : 'pointer')};
    text-decoration: none;
    transition: all 0.2s ease;
    line-height: ${tokens.typography.lineHeights[110]};
    gap: ${({ gap }) => gap ?? '5px'};
    min-width: ${({ isRounded, width }) => (isRounded ? '30px' : width ? 'auto' : '86px')};
    min-height: ${({ minHeight }) => minHeight ?? '30px'};
    width: ${({ width }) => width || 'auto'};
    max-height: ${({ maxHeight }) => maxHeight ?? '36px'};
    text-transform: ${({ textTransform }) => textTransform ?? 'capitalize'};

    /* Base styles - always apply */
    background: ${({ variant = 'primary', textOnly }) =>
        textOnly ? 'transparent' : buttonVariants[variant]?.background};
    color: ${({ variant = 'primary', textOnly, color }) => {
        if (textOnly) {
            return color ?? buttonVariants[variant]?.background;
        } else {
            return color ?? buttonVariants[variant]?.color;
        }
    }};
    /* Box mode styles - only when not text-only */
    ${({
        textOnly,
        variant = 'primary',
        outline,
        borderRadius = true,
        isRounded = false,
        padding,
        noAction = false,
        color,
    }) =>
        !textOnly &&
        `
    padding: ${isRounded ? '0' : (padding ?? '10px 15px')};
    border-radius: ${borderRadius ? (isRounded ? '50%' : globals['border-radius']) : 0};
    border: ${
        outline ? `1px solid ${colors.surfaces['border-divider']}` : `1px solid ${buttonVariants[variant]?.background}`
    };
    box-shadow : ${effects.effects.shadow.default};

    &:hover {
      background: ${noAction ? buttonVariants[variant]?.background : buttonVariants[variant]?.backgroundHover};
      border-color: ${
          outline
              ? colors.surfaces['border-divider']
              : noAction
                ? buttonVariants[variant]?.background
                : buttonVariants[variant]?.backgroundHover
      };
      box-shadow : ${effects.effects.shadow.hover};
      color : ${color ?? buttonVariants[variant]?.color};
    }

    &:active {
      background: ${noAction ? buttonVariants[variant]?.background : buttonVariants[variant]?.backgroundActive};
      border-color: ${
          outline
              ? colors.surfaces['border-divider']
              : noAction
                ? buttonVariants[variant]?.background
                : buttonVariants[variant]?.backgroundActive
      };
      box-shadow : ${effects.effects.shadow.hover};
    }

    /* Responsive styles - only height and padding changes */
    @media only screen and (max-width: 575px) {
      padding: ${isRounded ? '0' : '8px 10px'};
      font-size: ${tokens.typography.fontSize['sm-10px']};
    }

    @media only screen and (min-width: 576px) and (max-width: 767px) {
      padding: ${isRounded ? '0' : '10px 12px'};
      font-size: ${tokens.typography.fontSize['md-12px']};
    }

    @media only screen and (min-width: 768px) and (max-width: 991px) {
      padding: ${isRounded ? '0' : '10px 12px'};
      font-size: ${tokens.typography.fontSize['md-12px']};
    }
  `}

    /* Text-only mode styles */
  ${({ textOnly, variant = 'primary' }) =>
      textOnly &&
      `
    padding: 0;
    border: none;
    border-radius: 0;
    background: transparent !important;
    
    &:hover,
    &:focus {
      background: transparent;
      color: ${buttonVariants[variant]?.backgroundHover};
      text-decoration: underline;
    }

    &:active {
      background: transparent;
      color: ${buttonVariants[variant]?.backgroundActive};
    }
  `}
  
  
  &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: none;
    }
    label {
        padding: ${({ isRounded }) => (isRounded ? '0' : 'inherit')};
        display: ${({ isRounded }) => (isRounded ? 'flex' : 'inline-block')};
    }

    i {
        margin-right: 0;
        line-height: inherit;
        font-size: ${({ isRounded }) => (isRounded ? tokens.typography.fontSize['lg-14px'] : tokens.typography.fontSize['md-12px'])};
        color: ${({ isRounded }) => (isRounded ? colors.neutral.white : 'inherit')};
    }

    label i {
        margin-right: 0;
    }

    /* Full width on mobile if needed */
    ${({ block }) =>
        block &&
        `
    @media only screen and (max-width: 575px) {
      width: 100%;
    }
  `}
`;
